import { apiFetch } from './client';

export const PACK_KINDS = [
  { value: 'milk', label: 'Milk' },
  { value: 'water', label: 'Water' },
  { value: 'curd', label: 'Curd' },
];

export const DELIVERY_FEE_MODES = [
  { value: 'PER_DAY', label: 'Per day' },
  { value: 'PER_ITEM', label: 'Per item' },
  { value: 'ONE_TIME', label: 'One-time' },
];

export const DAYS_OF_WEEK = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

export const DROP_STATUS = {
  PENDING: 'PENDING',
  OUT: 'OUT',
  DELIVERED: 'DELIVERED',
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED',
  CANCELLED: 'CANCELLED',
};

export const DROP_STATUS_LABELS = {
  PENDING: 'Pending',
  OUT: 'Out',
  DELIVERED: 'Delivered',
  FAILED: 'Failed',
  SKIPPED: 'Skipped',
  CANCELLED: 'Cancelled',
};

export const SUBSCRIPTION_STATUSES = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'PENDING_PAYMENT', label: 'Pending payment' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'EXHAUSTED', label: 'Exhausted' },
];

/** Shown when deactivating a pack that customers still subscribe to. */
export const PACK_HAS_SUBSCRIBERS_MSG =
  "Can't deactivate — a customer still has an active or paused subscription on this pack.";

export const PACK_ERRORS = {
  invalid_delivery_fee_mode: 'Pick a valid delivery fee mode.',
  pack_unavailable: 'This pack is no longer available — refresh the list.',
  forbidden: 'You do not have permission to manage packs.',
  not_found: 'That pack could not be found.',
  invalid_name: 'Enter a pack name.',
  invalid_status: 'Status must be ACTIVE or INACTIVE.',
  invalid_price: 'Enter a valid price (0 or greater).',
  invalid_days_of_week: 'Pick at least one valid day of the week.',
  nothing_to_update: 'No changes to save.',
  invalid_numbers: 'Enter valid numbers for price and quantity.',
  name_kind_item_required: 'Name, kind, and catalog item are required.',
  item_unavailable: 'That catalog item is missing or inactive.',
  internal_error: 'Server error while saving the pack — try again, or ask backend to redeploy the pack PATCH fix.',
  pack_has_active_subscriptions: PACK_HAS_SUBSCRIBERS_MSG,
  pack_has_subscribers: PACK_HAS_SUBSCRIBERS_MSG,
  has_active_subscriptions: PACK_HAS_SUBSCRIBERS_MSG,
  active_subscriptions_exist: PACK_HAS_SUBSCRIBERS_MSG,
  cannot_deactivate: PACK_HAS_SUBSCRIBERS_MSG,
  pack_in_use: PACK_HAS_SUBSCRIBERS_MSG,
};

export function packHasActiveSubscriptions(pack) {
  if (!pack) return false;
  if (pack.hasActiveSubscriptions) return true;
  return Number(pack.activeSubscriptionCount || 0) > 0;
}

export function packErrorMessage(err) {
  const code = err?.code || err?.message;
  return (
    PACK_ERRORS[code]
    || err?.detail
    || err?.message
    || 'Could not update this pack.'
  );
}

export const DROP_ERRORS = {
  invalid_status: 'This drop is not ready for that action.',
  invalid_transition: 'This drop has already moved on — refresh to see its latest status.',
  not_found: 'This drop could not be found.',
  forbidden: 'You do not have permission to do this.',
  delivery_person_sub_required: 'Pick a rider before assigning.',
  rider_sub_required: 'Pick a rider before assigning.',
};

/** Calendar date YYYY-MM-DD in Asia/Kolkata. */
export function dateInIst(offsetDays = 0) {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
  if (!offsetDays) return today;
  const [y, m, d] = today.split('-').map(Number);
  const utcNoon = Date.UTC(y, m - 1, d + offsetDays, 12, 0, 0);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date(utcNoon));
}

export function formatAddress(addr) {
  if (!addr || typeof addr !== 'object') return null;
  if (typeof addr === 'string') return addr;
  const parts = [addr.line1, addr.line2, addr.area, addr.city, addr.pincode || addr.pin]
    .filter((p) => typeof p === 'string' && p.trim());
  return parts.length ? parts.join(', ') : null;
}

export function normalizePack(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const activeSubscriptionCount = Number(
    raw.active_subscription_count
    ?? raw.active_subscriptions
    ?? raw.subscriber_count
    ?? 0,
  );
  const hasActiveSubscriptions = !!(
    raw.has_active_subscriptions
    ?? raw.has_subscribers
    ?? (Number.isFinite(activeSubscriptionCount) && activeSubscriptionCount > 0)
  );
  return {
    id: raw.pack_id || raw.id || '',
    name: raw.name || '',
    kind: raw.kind || 'milk',
    itemId: raw.item_id || '',
    itemName: raw.item_name || raw.name || '',
    brand: raw.brand || raw.brand_name || '',
    qtyPerDrop: Number(raw.qty_per_drop ?? 1),
    price: Number(raw.price ?? 0),
    discountPercent: Number(raw.discount_percent ?? 0),
    deliveryFee: Number(raw.delivery_fee ?? 0),
    deliveryFeeMode: raw.delivery_fee_mode || 'PER_DAY',
    daysOfWeekDefault: Array.isArray(raw.days_of_week_default)
      ? raw.days_of_week_default
      : DAYS_OF_WEEK.map((d) => d.value),
    slotLabel: raw.slot_label || 'Morning',
    status: raw.status || 'ACTIVE',
    description: raw.description || '',
    activeSubscriptionCount: Number.isFinite(activeSubscriptionCount) ? activeSubscriptionCount : 0,
    hasActiveSubscriptions,
  };
}

function packToCreateBody(data) {
  return {
    name: data.name.trim(),
    kind: data.kind,
    item_id: data.itemId,
    qty_per_drop: Number(data.qtyPerDrop) || 1,
    price: Number(data.price) || 0,
    discount_percent: Number(data.discountPercent) || 0,
    delivery_fee: Number(data.deliveryFee) || 0,
    delivery_fee_mode: data.deliveryFeeMode || 'PER_DAY',
    days_of_week_default: data.daysOfWeekDefault?.length
      ? data.daysOfWeekDefault
      : DAYS_OF_WEEK.map((d) => d.value),
    ...(data.description ? { description: data.description.trim() } : {}),
  };
}

function packToPatchBody(data) {
  // Stage patch_pack accepts name, status, price, description, slot_label,
  // days_of_week_default, plus v2 fields (kind, item_id, fees, qty).
  // Attribute names are aliased server-side (DynamoDB reserves `name` / `status`).
  const body = {};
  if (data.name != null && String(data.name).trim()) {
    body.name = String(data.name).trim();
  }
  if (data.status != null) body.status = data.status;
  if (data.kind != null && String(data.kind).trim()) {
    body.kind = String(data.kind).trim().toLowerCase();
  }
  if (data.itemId != null && String(data.itemId).trim()) {
    body.item_id = String(data.itemId).trim();
  }
  if (data.price != null && data.price !== '') body.price = Number(data.price);
  if (data.description != null) body.description = String(data.description).trim();
  if (data.daysOfWeekDefault != null) {
    body.days_of_week_default = data.daysOfWeekDefault;
  }
  // v2 fields — ignored on older Stage; applied once pack PATCH supports them
  if (data.discountPercent != null && data.discountPercent !== '') {
    body.discount_percent = Number(data.discountPercent);
  }
  if (data.deliveryFee != null && data.deliveryFee !== '') {
    body.delivery_fee = Number(data.deliveryFee);
  }
  if (data.deliveryFeeMode != null) body.delivery_fee_mode = data.deliveryFeeMode;
  if (data.qtyPerDrop != null && data.qtyPerDrop !== '') {
    body.qty_per_drop = Number(data.qtyPerDrop);
  }
  return body;
}

export async function listPacks(token, { kind } = {}) {
  const qs = kind ? `?kind=${encodeURIComponent(kind)}` : '';
  const raw = await apiFetch(`/subscriptions/packs${qs}`, { token });
  return (raw?.packs || []).map(normalizePack).filter(Boolean);
}

export async function createPack(data, token) {
  const raw = await apiFetch('/store/subscriptions/packs', {
    method: 'POST',
    token,
    body: packToCreateBody(data),
  });
  return normalizePack(raw?.pack || raw);
}

export async function updatePack(packId, data, token) {
  const raw = await apiFetch(`/store/subscriptions/packs/${encodeURIComponent(packId)}`, {
    method: 'PATCH',
    token,
    body: packToPatchBody(data),
  });
  return normalizePack(raw?.pack || raw);
}

export function normalizeDrop(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const addr = raw.address || {};
  return {
    id: raw.drop_id || raw.id || '',
    orderId: raw.order_id || null,
    subscriptionId: raw.subscription_id || null,
    dropDate: raw.drop_date || null,
    status: raw.status || DROP_STATUS.PENDING,
    kind: raw.kind || null,
    itemId: raw.item_id || null,
    itemName: raw.item_name || '',
    brand: raw.brand || raw.brand_name || '',
    qty: Number(raw.qty ?? 0),
    customerPhone: raw.customer_phone || null,
    customerName: raw.customer_name || addr.name || addr.recipient_name || null,
    address: formatAddress(addr),
    addressRaw: addr,
    slotLabel: raw.slot_label || 'Morning',
    stockShort: !!raw.stock_short,
    deliveryPersonSub: raw.rider_sub || raw.delivery_person_sub || raw.assigned_to || null,
    riderName: raw.rider_name || raw.delivery_person_name || null,
  };
}

export function normalizeDropSummary(raw = {}) {
  return {
    pending: Number(raw.pending ?? 0),
    out: Number(raw.out ?? 0),
    delivered: Number(raw.delivered ?? 0),
    failed: Number(raw.failed ?? 0),
    skipped: Number(raw.skipped ?? 0),
    stockShort: Number(raw.stock_short ?? 0),
  };
}

export async function listDrops(date, token) {
  const targetDate = date || dateInIst(0);
  const qs = `?date=${encodeURIComponent(targetDate)}`;
  const raw = await apiFetch(`/store/subscriptions/drops${qs}`, { token });
  const drops = (raw?.drops || []).map(normalizeDrop).filter(Boolean);
  return {
    drops,
    summary: normalizeDropSummary(raw?.summary),
  };
}

async function dropAction(dropId, action, body, token) {
  const raw = await apiFetch(
    `/store/subscriptions/drops/${encodeURIComponent(dropId)}/${action}`,
    {
      method: 'POST',
      token,
      body: body || undefined,
    },
  );
  return normalizeDrop(raw?.drop || raw) || { id: dropId };
}

export function assignDrop(dropId, deliveryPersonSub, token) {
  // BE assign_drop reads `rider_sub` (not grocery `delivery_person_sub`).
  return dropAction(dropId, 'assign', { rider_sub: deliveryPersonSub }, token);
}

export function startDrop(dropId, token) {
  return dropAction(dropId, 'start', null, token);
}

export function deliverDrop(dropId, token) {
  return dropAction(dropId, 'deliver', null, token);
}

export function failDrop(dropId, reason, token) {
  return dropAction(dropId, 'fail', reason ? { reason } : null, token);
}

export function normalizeSubscription(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    id: raw.subscription_id || raw.id || '',
    status: raw.status || '',
    packId: raw.pack_id || null,
    packName: raw.pack_name || '',
    kind: raw.kind || null,
    qtyPerDay: Number(raw.qty_per_day ?? raw.qty_per_drop ?? 0),
    prepaidTotal: Number(raw.prepaid_total ?? 0),
    prepaidRemaining: Number(raw.prepaid_remaining ?? 0),
    amountPayable: Number(raw.amount_payable ?? 0),
    slotLabel: raw.slot_label || 'Morning',
    customerPhone: raw.customer_phone || null,
    address: formatAddress(raw.address),
    deliveryDates: Array.isArray(raw.delivery_dates) ? raw.delivery_dates : [],
    scheduleMode: raw.schedule_mode || null,
    paymentStatus: raw.payment_status || null,
    cancelRequestedAt: raw.cancel_requested_at || null,
    cancelNoticeUntil: raw.cancel_notice_until || null,
    pauseFrom: raw.pause_from || null,
    pauseUntil: raw.pause_until || null,
  };
}

export async function listStoreSubscriptions(token, { status } = {}) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const raw = await apiFetch(`/store/subscriptions${qs}`, { token });
  return (raw?.subscriptions || []).map(normalizeSubscription).filter(Boolean);
}
